import {handleSaveDraftActivity, handleCreateActivity, handleUpdateDraftActivity, handleUpdateActivity, validateActivityName} from '@/services/activities'
import {useAppDispatch, useAppSelector} from '@/store'
import {resetActivityData, setActivityFormData} from '@/store/slices/activitySlice'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ACTIVITY_TYPE, ACTIVITY_TYPES_ENUM, OrderType} from '@/types/Activity'
import {getDateFromISOString, getFormattedDateString, getUUID, formatUTCTimestamp, isPublished} from '@/utils/commons'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {COUNTRY_CODES_FREQUENCY, APPLICATION_ID} from '@/utils/constants/globalConstants'
import { useActivityValidation } from '@/hooks/useActivityValidation'

export default function useActivityReview() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isCancel, setIsCancel] = useState(false)
  const activityFormData = useSelector(state => state.activity.formData)
  const [isNameValid, setIsNameValid] = useState(true)
  
  const {
    showToast,
    toastMessage,
    handleActivityDurationValidation,
    handleToastClose,
    resetValidation,
    handleViewActivity,
  } = useActivityValidation()

  const getActivityEnum = activityType => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.WELCOME
    }
    if (activityType === ACTIVITY_TYPE.LOGIN) {
      return ACTIVITY_TYPES_ENUM.LOGIN
    }
    if (activityType === ACTIVITY_TYPE.PLAY_GAME) {
      return ACTIVITY_TYPES_ENUM.PLAY_GAME
    }
    if (activityType === ACTIVITY_TYPE.WATCH_VIDEO) {
      return ACTIVITY_TYPES_ENUM.WATCH_VIDEO
    }
    if (activityType === ACTIVITY_TYPE.SURVEY) {
      return ACTIVITY_TYPES_ENUM.SURVEY
    }
    if (activityType === ACTIVITY_TYPE.POINTS_TRANSFER) {
      return ACTIVITY_TYPES_ENUM.POINTS_TRANSFER
    }
    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY
    }
    if (activityType === ACTIVITY_TYPE.REDEEM_POINTS) {
      return ACTIVITY_TYPES_ENUM.REDEEM_POINTS
    }
    if (activityType === ACTIVITY_TYPE.GENERIC_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY
    }
    return null
  }

  const getDisplayActivityType = activityType => {
    switch(activityType){
      case ACTIVITY_TYPES_ENUM.WELCOME:  return ACTIVITY_TYPE.WELCOME_ACTIVITY
      case ACTIVITY_TYPES_ENUM.LOGIN: return ACTIVITY_TYPE.LOGIN
      case ACTIVITY_TYPES_ENUM.PLAY_GAME: return ACTIVITY_TYPE.PLAY_GAME
      case ACTIVITY_TYPES_ENUM.WATCH_VIDEO: return ACTIVITY_TYPE.WATCH_VIDEO
      case ACTIVITY_TYPES_ENUM.SURVEY: return ACTIVITY_TYPE.SURVEY
      case ACTIVITY_TYPES_ENUM.POINTS_TRANSFER: return ACTIVITY_TYPE.POINTS_TRANSFER
      case ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY: return ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY
      case ACTIVITY_TYPES_ENUM.REDEEM_POINTS: return ACTIVITY_TYPE.REDEEM_POINTS
      case ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY: return ACTIVITY_TYPE.GENERIC_ACTIVITY
      default: return activityType
    }
    
  }
  const getSlug = activityType => {
    return `reward-points::${activityType.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`
  }

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false,
    }
    dispatch(resetActivityData(defaultFormData))
  }

  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const typeOfActivity = ['Welcome Activity', 'Login']

  const getActivityName = () => {
    const { activityType, uuid, activityName } = activityFormData || {}
    
    // For Points on Order Delivery, always use the activity name from form data
    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return activityName
    }

    // Generate timestamp-based name for:
    // 1. New activities without UUID
    // 2. Welcome/Login activities without a custom name
    const shouldUseTimestampName = 
      !uuid || (typeOfActivity.includes(activityType) && !activityName)
    
    if (shouldUseTimestampName) {
      return `${getActivityEnum(activityType)}_${formatUTCTimestamp()}`
    }

    // Use custom activity name if available
    return activityName
  }


const handleNameValidation = async (name) => {
  if (!name?.trim()) {
    return { success: false, error: 'Activity name cannot be empty' }
  }
  const uuid = activityFormData?.uuid ?? null
  try {
    const response = await validateActivityName(name, uuid)
    if (response?.success !== true) {
      return { success: false, error: 'Activity with same name already exists' }
    }
    return { success: true }
  } catch (error) {
    console.error('Activity name validation error', error)
    return { success: false, error: 'Activity with same name already exists' }
  }
}

  const validateName = async name => {
    if (activityFormData?.activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      const resp = await handleNameValidation(name)
      const isValid = resp?.success === true
      setIsNameValid(isValid)
      return isValid
    } else {
      setIsNameValid(true)
      return true
    }
  }



  const handlePublish = async () => { 
    const uniqueActivityName = getActivityName()
    const isValidName = await validateName(uniqueActivityName)
    if (!isValidName) return

    // Check if this is editing a published activity
    const isEditingPublished = isPublished(activityFormData?.status) && activityFormData?.id
    
    // Add duration validation before publishing (skip for PRODUCT_PURCHASE activity type)
    if (activityFormData?.activityType !== ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      let validationParams = {...activityFormData, activityName: uniqueActivityName}
      if(isEditingPublished){
        validationParams.id = activityFormData?.id;
      } else {
        delete validationParams.id;
      }
      const isDurationValid = await handleActivityDurationValidation(validationParams)
      if (!isDurationValid) return
    }
    
    // these are the required fields for Login & Welcome API call
      const param = {
        applicationId: APPLICATION_ID[countryCode] || 1,
        title: '',
        activityType: getActivityEnum(activityFormData?.activityType), // WELCOME | LOGIN | POINTS_ON_ORDER_DELIVERY
        tags: '',
        slug: getSlug(activityFormData?.activityType), //common
        pointsEarned: activityFormData?.pointsEarned, // common
        maxLimitPerUser: 0,
        dailyLoginLimit: activityFormData?.dailyLoginLimit, // Login
        conversionRate: 0,
        amountPerUnit: 0,
        pointsAwardedPerUnit: 0,
        activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
        activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
        allTimeLoginLimit: activityFormData?.allTimeLoginLimit, // Login
        activityName: uniqueActivityName,
        ...(!isEditingPublished && { uuid: activityFormData?.uuid || getUUID(uniqueActivityName) }), // Use the determined final UUID
        orderType: OrderType[activityFormData?.orderType],
        localCurrency: activityFormData?.localCurrency,
        ...(COUNTRY_CODES_FREQUENCY.includes(countryCode)
          ? {numberOfCases: activityFormData?.numberOfCases}
          : {localCurrency: activityFormData?.localCurrency}),
        hasEndDate: activityFormData?.hasEndDate,
        receivePoints: activityFormData?.receivePoints,
        status: 'PUBLISHED',
        ...(isEditingPublished && { activityName: activityFormData.activityName }),
        ...(isEditingPublished && { id: activityFormData.id }), // Include ID when editing published activity
      }

      const formBasedParams = Object.fromEntries(
        Object.entries(param).filter(([key, value]) => {
          const isFromFormData =
            key in activityFormData ||
            value === getActivityEnum(activityFormData?.activityType) ||
            value === getSlug(activityFormData?.activityType) ||
            value === getFormattedDateString(activityFormData?.startDate) ||
            value === getFormattedDateString(activityFormData?.endDate) ||
            value === OrderType[activityFormData?.orderType]

          const isManuallyIncluded = ['tags', 'applicationId', 'slug', 'activityName', 'uuid', 'status', 'id'].includes(key)
          if (isManuallyIncluded) {
            return true
          }

          return isFromFormData && value !== undefined && value !== null && value !== ''
        })
      )

      try {
        const response = isEditingPublished 
          ? await handleUpdateActivity({data: formBasedParams})
          : await handleCreateActivity({data: formBasedParams})
        
        if (response?.success) {
          dispatch(setActivityFormData(activityFormData))
          dispatch(setToastNotifications({show: true, message: isEditingPublished ? 'Activity updated successfully' : 'New Activity published', type: 'success'}))
          resetValidation() // Clear any validation toast state
          handleResetFormData()
          navigate(ROUTES.ACTIVITIES + '?tab=0')
        } else {
          const errMsg = response?.errorMessage || 'An unknown error occurred'
          dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
        }
      } catch (error) {
        const errMsg = error?.response?.data?.errorMessage || error?.message || 'An unknown error occurred'
        dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
      }
  }

  const handleDraft = async () => {
    const uniqueActivityName = getActivityName()
    const isValidName = await validateName(uniqueActivityName)
    if (!isValidName) return
    else{
    let response

    if (!activityFormData?.uuid) {
      response = await handleSaveDraftActivity({
        data: {
          ...activityFormData,
          activityName: uniqueActivityName,
          activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
          activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
          activityType: getActivityEnum(activityFormData?.activityType),
          status: 'DRAFT',
          uuid: getUUID(uniqueActivityName), // Use consistent UUID
        },
      })
    } else {
      response = await handleUpdateDraftActivity({
        data: {
          ...activityFormData,
          activityName: uniqueActivityName,
          activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
          activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
          activityType: getActivityEnum(activityFormData?.activityType),
          status: 'DRAFT',
          uuid: activityFormData?.uuid, // Use consistent UUID
        },
      })
    }

    if (response?.success) {
      dispatch(
        setToastNotifications({
          show: true,
          message: activityFormData?.uuid ? 'Activity draft updated' : 'New Activity draft',
          type: 'success',
        })
      )
      handleResetFormData()
      navigate(ROUTES.ACTIVITIES + '?tab=0')
    } else {
      dispatch(setToastNotifications({show: true, message: 'Failed to save draft', type: 'error'}))
    }}
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  const onClickPrimaryButton = () => {
    handleResetFormData()
    navigate(ROUTES.ACTIVITIES + '?tab=0')
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }

  const handleValidationModalClose = () => {
    setIsNameValid(true)
  }



  const getFormDataWithOrder = formDataFromApi => {

    let {
      activityType,
      pointsEarned,
      hasEndDate,
      activityStartDate: startDate,
      activityEndDate: endDate,
      dailyLoginLimit,
      allTimeLoginLimit,
      activityName,
      orderType,
      localCurrency,
      numberOfCases,
      receivePoints,
      status,
      uuid,
      activityId, // Extract activityId instead of id
    } = formDataFromApi

    //Removing this condition as getFormDataWithOrder function conditionally maps activeStartDate to startDate only when the status is 'Published'. This conditional mapping could lead to startDate being undefined in other statuses.

    startDate = formDataFromApi?.activeStartDate || formDataFromApi?.activityStartDate
    endDate = formDataFromApi?.activeEndDate || formDataFromApi?.activityEndDate
    // Ensure activeStartDate is always mapped to startDate
    startDate = formDataFromApi?.activeStartDate || formDataFromApi?.activityStartDate
    endDate = formDataFromApi?.activeEndDate || formDataFromApi?.activityEndDate
    if (pointsEarned) {
      pointsEarned = String(pointsEarned)
    }
    if (numberOfCases) {
      numberOfCases = String(numberOfCases)
    }
    if (receivePoints) {
      receivePoints = String(receivePoints)
    }
    startDate = getDateFromISOString(startDate)
    if (endDate) endDate = getDateFromISOString(endDate)
    activityType = getDisplayActivityType(activityType)
    const isCountryCodeFrequency = COUNTRY_CODES_FREQUENCY.includes(countryCode)
    // Order alignment for Welcome review page
    const welcomeActivityOrder = {
      activityType: activityType,
      pointsEarned: pointsEarned,
      hasEndDate: hasEndDate,
      startDate: startDate,
      ...(activityName && {activityName: activityName}),
      ...(uuid && {uuid: uuid}),
      ...(status && {status: status}),
      ...(activityId && {id: activityId}), // Map activityId to id in form data
      ...(hasEndDate && {endDate: endDate}),
    }

    // Order alignment for Login review page
    const loginActivityOrder = {
      activityType: activityType,
      pointsEarned: pointsEarned,
      ...(dailyLoginLimit && {dailyLoginLimit: dailyLoginLimit}),
      ...(allTimeLoginLimit && {allTimeLoginLimit: allTimeLoginLimit}),
      startDate: startDate,
      endDate: endDate,
      receivePoints: receivePoints,
      ...(activityName && {activityName: activityName}),
      ...(uuid && {uuid: uuid}),
      ...(status && {status: status}),
      ...(activityId && {id: activityId}), // Map activityId to id in form data
    }

    const pointsOnOrderDeliveryActivityOrder = () => {
      const fieldsArranged = {
        activityType: activityType,
        activityName: activityName,
        orderType: orderType,
        uuid: uuid,
        status: status,
        id: activityId, // Map activityId to id in form data
        startDate: startDate,
        endDate: endDate,
      }

      // Add frequency-based fields
        if (isCountryCodeFrequency) {
        fieldsArranged.numberOfCases = numberOfCases
          fieldsArranged.pointsEarned = pointsEarned
      }
      else {
        fieldsArranged.localCurrency = localCurrency
          fieldsArranged.pointsEarned = pointsEarned
      }

      // Filter out any undefined or null values
      return Object.entries(fieldsArranged)
        .reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value;
          }
          return acc;
        }, {});
    }

    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return welcomeActivityOrder
    } else if (activityType === ACTIVITY_TYPE.LOGIN) {
      return loginActivityOrder
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
       return pointsOnOrderDeliveryActivityOrder()
    } else {
      return {}
    }
  }

  return {
    handlePublish,
    handleDraft,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    isCancel,
    activityFormData,
    getFormDataWithOrder,
    isNameValid,
    handleValidationModalClose,
    validateName,
    getActivityName,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
  }
}
